import { useApolloClient } from '@apollo/client/react'
import {
  Alert,
  Box,
  Button,
  Flex,
  Icon,
  ScrollArea,
  SegmentGroup,
  Skeleton,
  VStack,
} from '@chakra-ui/react'
import { parseAsStringLiteral, useQueryState } from 'nuqs'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LuBellOff } from 'react-icons/lu'

import { EmptyState } from '@/components/ui/empty-state'
import { Pagination } from '@/components/ui/pagination'
import {
  NotificationsDocument,
  NotificationsQuery,
  NotificationsQueryVariables,
} from '@/graphql/api/generated'
import {
  isNotificationUnread,
  useNotifications,
} from '@/hooks/useNotifications'
import { usePagination } from '@/hooks/usePagination'
import { UserRoutes } from '@/routes/_hooks/routes.enum'
import { Link } from '@/routing'

import { NotificationItem } from './NotificationItem'

export type NotificationsReadFilterValue = 'unread' | 'read'

const READ_FILTERS = ['unread', 'read'] as const
const PAGE_SIZE = 20
/** Safety cap when marking every unread notification as read in one click. */
const MARK_ALL_MAX = 1000

interface NotificationsListProps {
  /**
   * 'popover' (default): compact, capped height, "View all" footer button.
   * 'page': roomier layout for /notifications, with a cursor pager and the tab in the URL.
   */
  variant?: 'popover' | 'page'
}

export function NotificationsList({
  variant = 'popover',
}: NotificationsListProps) {
  const { t } = useTranslation('notifications')
  const client = useApolloClient()
  const isPage = variant === 'page'

  const {
    variables: pagerVars,
    goToNextPage,
    goToPreviousPage,
    resetPagination,
  } = usePagination({ pageSize: PAGE_SIZE })

  // Page: the active tab lives in the URL (?tab=read) so refresh and deep links keep it.
  const [urlFilter, setUrlFilter] = useQueryState(
    'tab',
    parseAsStringLiteral(READ_FILTERS).withDefault('unread')
  )
  // Popover: local-only filter (must not mutate the current page URL).
  const [localFilter, setLocalFilter] =
    useState<NotificationsReadFilterValue>('unread')
  const readFilter = isPage ? urlFilter : localFilter
  const setReadFilter = (value: NotificationsReadFilterValue) => {
    // Reset the cursor synchronously with the filter change so the next query never fires
    // with the previous tab's (now invalid) cursor.
    resetPagination()
    if (isPage) {
      void setUrlFilter(value)
    } else {
      setLocalFilter(value)
    }
  }

  const {
    notifications,
    unreadCount,
    totalCount,
    pageInfo,
    markAsRead,
    markAsReadLoading,
    loading,
    error,
    currentUserId,
  } = useNotifications({
    read: readFilter,
    first: isPage ? pagerVars.first : 30,
    afterCursor: isPage ? pagerVars.afterCursor : undefined,
    beforeCursor: isPage ? pagerVars.beforeCursor : undefined,
    showToast: !isPage,
  })

  // If a paginated page (cursor set) ends up empty — e.g. after marking everything on it as
  // read — fall back to the first page instead of stranding the user on an empty view.
  useEffect(() => {
    if (
      isPage &&
      !loading &&
      notifications.length === 0 &&
      (pagerVars.afterCursor != null || pagerVars.beforeCursor != null)
    ) {
      resetPagination()
    }
  }, [
    isPage,
    loading,
    notifications.length,
    pagerVars.afterCursor,
    pagerVars.beforeCursor,
    resetPagination,
  ])

  const handleMarkAllAsRead = async () => {
    // Mark ALL unread notifications, not just the loaded page: fetch every unread id once
    // (capped), then mark them in a single call.
    const { data } = await client.query<
      NotificationsQuery,
      NotificationsQueryVariables
    >({
      query: NotificationsDocument,
      variables: { read: 'unread', first: Math.min(totalCount, MARK_ALL_MAX) },
      fetchPolicy: 'network-only',
    })
    const ids = (data?.notifications?.edges ?? [])
      .map((edge) => edge?.node?.id)
      .filter((id): id is string => !!id)
    if (ids.length > 0) await markAsRead(ids)
  }

  const filterTabs = (
    <SegmentGroup.Root
      value={readFilter}
      onValueChange={(e) =>
        setReadFilter((e.value as NotificationsReadFilterValue) ?? 'unread')
      }
      size="sm"
    >
      <SegmentGroup.Indicator />
      <SegmentGroup.Item value="unread">
        <SegmentGroup.ItemText>{t('filterUnread')}</SegmentGroup.ItemText>
        <SegmentGroup.ItemHiddenInput />
      </SegmentGroup.Item>
      <SegmentGroup.Item value="read">
        <SegmentGroup.ItemText>{t('filterRead')}</SegmentGroup.ItemText>
        <SegmentGroup.ItemHiddenInput />
      </SegmentGroup.Item>
    </SegmentGroup.Root>
  )

  const markAllButton =
    unreadCount > 0 ? (
      <Button
        size="xs"
        variant="ghost"
        onClick={handleMarkAllAsRead}
        loading={markAsReadLoading}
      >
        {t('markAllAsRead')}
      </Button>
    ) : null

  const itemsList = (
    <VStack align="stretch" gap={isPage ? 2 : 1} p={3} pb={4}>
      {notifications.map(
        (n) =>
          n && (
            <NotificationItem
              key={n.id}
              notification={n}
              isUnread={isNotificationUnread(n, currentUserId)}
              onMarkAsRead={(id, read) => markAsRead([id], read)}
            />
          )
      )}
    </VStack>
  )

  const stateContent = loading ? (
    <VStack align="stretch" gap={2} p={isPage ? 4 : 3}>
      {[1, 2, 3].map((i) => (
        <Skeleton key={i} height="72px" borderRadius="md" />
      ))}
    </VStack>
  ) : error ? (
    <Box p={isPage ? 4 : 3}>
      <Alert.Root status="error" variant="subtle">
        <Alert.Indicator />
        <Alert.Title>
          {t('error', { defaultValue: 'Error loading notifications' })}
        </Alert.Title>
      </Alert.Root>
    </Box>
  ) : notifications.length === 0 ? (
    <Box p={isPage ? 12 : 3}>
      <EmptyState
        title={readFilter === 'read' ? t('emptyRead') : t('empty')}
        icon={
          <Icon
            as={LuBellOff}
            boxSize="8"
            color="gray.400"
            _dark={{ color: 'gray.500' }}
          />
        }
        titleProps={{ color: 'gray.400', _dark: { color: 'gray.500' } }}
      />
    </Box>
  ) : null

  const pagerPageInfo = pageInfo
    ? {
        hasNextPage: pageInfo.hasNextPage,
        hasPreviousPage: pageInfo.hasPreviousPage,
        startCursor: pageInfo.startCursor ?? undefined,
        endCursor: pageInfo.endCursor ?? undefined,
      }
    : undefined
  const showPager =
    isPage &&
    pagerPageInfo !== undefined &&
    (pagerPageInfo.hasNextPage || pagerPageInfo.hasPreviousPage)

  if (isPage) {
    return (
      <VStack align="stretch" gap={0}>
        <Flex
          justify="space-between"
          align="center"
          gap={3}
          wrap="wrap"
          px={4}
          py={3}
          borderBottomWidth="1px"
          borderColor="border"
        >
          {filterTabs}
          {markAllButton}
        </Flex>
        {stateContent ?? itemsList}
        {showPager && (
          <Box px={4} pb={4} borderTopWidth="1px" borderColor="border">
            <Pagination
              pageInfo={pagerPageInfo}
              totalCount={totalCount}
              pageSize={PAGE_SIZE}
              onNextPage={goToNextPage}
              onPreviousPage={goToPreviousPage}
              showPageSizeSelector={false}
            />
          </Box>
        )}
      </VStack>
    )
  }

  return (
    <VStack align="stretch" gap={0} flex="1" minH="0">
      <Box px={3} pt={2} pb={2}>
        {filterTabs}
      </Box>

      {stateContent ?? (
        <Box flex="1" minH="0" display="flex" flexDirection="column">
          {markAllButton && (
            <Box px={3} pt={1} pb={2}>
              {markAllButton}
            </Box>
          )}
          <ScrollArea.Root
            overflowY="auto"
            flex={{ base: 1, md: 'initial' }}
            minH={{ base: 0 }}
            maxH={{ base: '100%', md: 'min(400px, 60vh)' }}
          >
            <ScrollArea.Viewport>{itemsList}</ScrollArea.Viewport>
            <ScrollArea.Scrollbar orientation="vertical">
              <ScrollArea.Thumb />
            </ScrollArea.Scrollbar>
          </ScrollArea.Root>
        </Box>
      )}

      <Box px={3} py={2} borderTopWidth="1px" borderColor="border">
        <Button size="sm" variant="ghost" w="full" asChild>
          <Link to={`${UserRoutes.MyNotifications}?tab=${readFilter}`}>
            {t('viewAll')}
          </Link>
        </Button>
      </Box>
    </VStack>
  )
}
